import { useState, useCallback, useMemo, useEffect } from 'react';
import { convertToMinutes } from '../utils/triggerOptions';

export const useCampaignCard = ({ 
	sequences = [], 
	status, 
	slug, 
	onUpdate 
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editingSequences, setEditingSequences] = useState([]);
	const [hasSaved, setHasSaved] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [sequenceValidation, setSequenceValidation] = useState({});

	// Maximum number of sequences allowed for manual addition
	const MAX_SEQUENCES = 3;

	// Initialize editing sequences when entering edit mode
	const handleStartEditing = useCallback(() => {
		setIsEditing(true);
		
		// Sort sequences by their sequence number to ensure correct order
		const sortedSequences = sequences.length > 0 
			? [...sequences].sort((a, b) => a.sequence - b.sequence)
			: [{
				id: Date.now(),
				title: '',
				message: '',
				trigger_value: 2,
				trigger_unit: 'MINUTES',
				trigger_time_minutes: 2,
				sequence: 1,
			}];
		
		setEditingSequences(sortedSequences);
		setHasSaved(false);
		setSequenceValidation({}); // Reset validation state
	}, [sequences, slug]);

	// Handle saving all changes
	const handleSave = useCallback(() => {
		// Check if all sequences are valid before saving
		const hasValidationErrors = Object.values(sequenceValidation).some(isValid => isValid === false);
		
		if (hasValidationErrors) {
			console.log('Cannot save: validation errors exist', sequenceValidation);
			return;
		}

		const validSequences = editingSequences
			.filter(seq => {
				// Only include sequences that have both title and message
				const hasTitle = seq.title && seq.title.toString().trim();
				const hasMessage = seq.message && seq.message.toString().trim();
				return hasTitle && hasMessage;
			})
			.map((seq, index) => {
				// Calculate trigger_time_minutes
				const trigger_time_minutes = convertToMinutes(
					parseInt(seq.trigger_value || 2), 
					seq.trigger_unit || 'MINUTES'
				);

				// For new sequences, don't include the temporary ID
				const cleanSequence = { ...seq };
				if (typeof cleanSequence.id === 'number' && cleanSequence.id > 1000000000000) {
					// Remove temporary ID for new sequences
					delete cleanSequence.id;
				}
				
				return {
					...cleanSequence,
					sequence: index + 1, // Ensure proper sequence numbering
					trigger_time_minutes,
					trigger_value: parseInt(seq.trigger_value || 2),
				};
			});

		// Don't save if no valid sequences
		if (validSequences.length === 0) {
			console.log('Cannot save: no valid sequences');
			return;
		}

		const updatePayload = {
			slug,
			sequences: validSequences,
			status: status, // Keep current status
		};
		
		setHasSaved(true);
		onUpdate(updatePayload);
	}, [editingSequences, slug, status, onUpdate, sequenceValidation]);

	// Handle canceling edit
	const handleCancel = useCallback(() => {
		setIsEditing(false);
		setEditingSequences([]);
		setHasSaved(false);
		setSequenceValidation({});
	}, []);

	// Handle sequence updates (only update local state, no auto-save)
	const handleSequenceUpdate = useCallback((index, field, value) => {
		if (field === 'isValid') {
			// Update validation status for this sequence
			setSequenceValidation(prev => ({
				...prev,
				[index]: value
			}));
			return;
		}

		setEditingSequences(prev => {
			const updated = [...prev];
			if (updated[index]) {
				updated[index] = {
					...updated[index],
					[field]: value
				};
			}
			return updated;
		});
	}, []);

	// Handle sequence deletion
	const handleSequenceDelete = useCallback((index) => {
		setEditingSequences(prev => prev.filter((_, i) => i !== index));
		
		// Remove validation status for deleted sequence and reindex remaining ones
		setSequenceValidation(prev => {
			const newValidation = {};
			Object.keys(prev).forEach(key => {
				const keyIndex = parseInt(key);
				if (keyIndex < index) {
					// Keep validation for sequences before deleted one
					newValidation[keyIndex] = prev[key];
				} else if (keyIndex > index) {
					// Reindex validation for sequences after deleted one
					newValidation[keyIndex - 1] = prev[key];
				}
				// Skip validation for the deleted sequence (keyIndex === index)
			});
			return newValidation;
		});
	}, []);

	// Add new sequence - only allow if under MAX_SEQUENCES limit
	const handleAddSequence = useCallback(() => {
		// Only allow adding if current count is less than MAX_SEQUENCES
		if (editingSequences.length >= MAX_SEQUENCES) return;
		
		const newSequence = {
			id: Date.now(), // Temporary ID for new sequences
			title: '',
			message: '',
			trigger_value: 2,
			trigger_unit: 'MINUTES',
			trigger_time_minutes: 2,
			sequence: editingSequences.length + 1,
		};
		setEditingSequences(prev => [...prev, newSequence]);
		
		// Set validation status for new sequence as invalid initially
		setSequenceValidation(prev => ({
			...prev,
			[editingSequences.length]: false
		}));
	}, [editingSequences.length]);

	// Toggle campaign status
	const handleToggleStatus = useCallback(() => {
		const newStatus = status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
		const updatePayload = {
			slug,
			sequences,
			status: newStatus,
		};
		
		onUpdate(updatePayload);
	}, [slug, sequences, status, onUpdate]);

	// Status display logic
	const statusConfig = useMemo(() => {
		switch (status) {
			case 'ACTIVE':
				return {
					label: 'Active',
					color: '#009966',
					bgColor: 'bg-green-50',
				};
			case 'PAUSED':
				return {
					label: 'Paused',
					color: '#f0b100',
					bgColor: 'bg-yellow-50',
				};
			default:
				return {
					label: 'Setup Required',
					color: '#6b7280',
					bgColor: 'bg-gray-50',
				};
		}
	}, [status]);

	// Check if save should be enabled
	const canSave = useMemo(() => {
		// Must have at least one sequence with content
		const hasValidSequences = editingSequences.some(seq => 
			seq.title && seq.title.toString().trim() && 
			seq.message && seq.message.toString().trim()
		);
		
		// All sequences must be valid (no validation errors)
		const allSequencesValid = Object.values(sequenceValidation).every(isValid => isValid !== false);
		
		// Must have validation status for all sequences with content
		const sequencesWithContent = editingSequences.filter(seq => 
			seq.title && seq.title.toString().trim() && 
			seq.message && seq.message.toString().trim()
		);
		
		const hasValidationForAllSequences = sequencesWithContent.every((_, index) => 
			sequenceValidation.hasOwnProperty(index)
		);
		
		return hasValidSequences && allSequencesValid && hasValidationForAllSequences;
	}, [editingSequences, sequenceValidation]);

	const hasSequences = sequences.length > 0;

	return {
		// State
		isEditing,
		editingSequences,
		hasSaved,
		isDropdownOpen,
		statusConfig,
		hasSequences,
		canSave,
		MAX_SEQUENCES,
		
		// Setters
		setIsEditing,
		setEditingSequences,
		setHasSaved,
		setIsDropdownOpen,
		
		// Handlers
		handleStartEditing,
		handleSave,
		handleCancel,
		handleSequenceUpdate,
		handleSequenceDelete,
		handleAddSequence,
		handleToggleStatus,
	};
}; 